import React from 'react';
import type { BilingualText } from '@/types';

interface Props {
  label: string;
  value: BilingualText;
  onChange: (val: BilingualText) => void;
  textarea?: boolean;
  placeholder?: string;
  /** Placeholder for the Georgian box; falls back to `placeholder`. */
  placeholderKa?: string;
  maxLength?: number;
  rows?: number;
}

const AdminBilingualInput: React.FC<Props> = ({ label, value, onChange, textarea, placeholder, placeholderKa, maxLength, rows = 4 }) => {
  const cls = "w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20 resize-none";

  const handleChange = (lang: 'en' | 'ka', raw: string): void => {
    const val = maxLength ? raw.slice(0, maxLength) : raw;
    onChange({ ...value, [lang]: val });
  };

  const counter = (text: string): React.ReactNode => {
    if (!maxLength) return null;
    const len = text.length;
    const near = len >= maxLength * 0.9;
    const at = len >= maxLength;
    return (
      <span className={`text-[8px] font-bold tabular-nums ${at ? 'text-red-500' : near ? 'text-amber-500' : 'text-black/15 dark:text-white/15'}`}>
        {len}/{maxLength}
      </span>
    );
  };

  return (
    <div>
      <label className="block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2">{label}</label>
      <div className="grid grid-cols-2 gap-2">
        <div className="relative">
          <span className="absolute top-2.5 right-3 flex items-center gap-2 text-[8px] font-bold uppercase tracking-wider text-black/15 dark:text-white/15">
            {counter(value.en)} EN
          </span>
          {textarea ? (
            <textarea value={value.en} onChange={(e) => handleChange('en', e.target.value)} placeholder={placeholder} rows={rows} className={cls} />
          ) : (
            <input value={value.en} onChange={(e) => handleChange('en', e.target.value)} placeholder={placeholder} className={cls} />
          )}
        </div>
        <div className="relative">
          <span className="absolute top-2.5 right-3 flex items-center gap-2 text-[8px] font-bold uppercase tracking-wider text-black/15 dark:text-white/15">
            {counter(value.ka)} KA
          </span>
          {textarea ? (
            <textarea value={value.ka} onChange={(e) => handleChange('ka', e.target.value)} placeholder={placeholderKa ?? placeholder} rows={rows} className={cls} />
          ) : (
            <input value={value.ka} onChange={(e) => handleChange('ka', e.target.value)} placeholder={placeholderKa ?? placeholder} className={cls} />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBilingualInput;
